import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { messagesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
    MessageSquare, Send, ArrowLeft, Calendar, Clock, User,
    Loader2, CheckCircle, Bell
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function MessagesPage() {
    const { user } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);

    const loadConversations = useCallback(async () => {
        try {
            const result = await messagesApi.getConversations();
            setConversations(result.conversations || []);
        } catch (error) {
            console.error('Error loading conversations:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadMessages = useCallback(async (conversationId) => {
        try {
            const result = await messagesApi.getConversation(conversationId);
            setMessages(result.messages || []);
            setSelectedConversation(result.conversation);
        } catch (error) {
            console.error('Error loading messages:', error);
            toast.error('Failed to load messages');
        }
    }, []);

    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedConversation) return;
        
        setIsSending(true);
        try {
            await messagesApi.sendMessage({
                conversation_id: selectedConversation.conversation_id,
                content: newMessage.trim()
            });
            setNewMessage('');
            // Reload messages
            await loadMessages(selectedConversation.conversation_id);
            toast.success('Message sent');
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setIsSending(false);
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="min-h-screen bg-void text-white">
            {/* Header */}
            <header className="border-b border-border bg-void-paper">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors">
                            <ArrowLeft className="w-4 h-4" />
                            <span className="font-mono text-sm">Back to Dashboard</span>
                        </Link>
                        <div className="flex items-center gap-2">
                            <Bell className="w-5 h-5 text-acid" />
                            <h1 className="font-display text-xl font-bold">Messages</h1>
                        </div>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
                    {/* Conversations List */}
                    <Card className="bg-void-paper border-border lg:col-span-1">
                        <CardHeader className="pb-3">
                            <CardTitle className="font-display text-lg flex items-center gap-2">
                                <MessageSquare className="w-5 h-5 text-cyber-purple" strokeWidth={1.5} />
                                Conversations
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-6 h-6 text-acid animate-spin" />
                                </div>
                            ) : conversations.length === 0 ? (
                                <div className="text-center py-12">
                                    <MessageSquare className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                                    <p className="text-muted-foreground text-sm">No messages yet</p>
                                    <p className="text-muted-foreground/50 text-xs mt-1">
                                        Employers will contact you here
                                    </p>
                                </div>
                            ) : (
                                <ScrollArea className="h-[500px]">
                                    <div className="space-y-2">
                                        {conversations.map((conv) => (
                                            <div
                                                key={conv.conversation_id}
                                                className={`p-3 rounded-sm border cursor-pointer transition-all ${
                                                    selectedConversation?.conversation_id === conv.conversation_id
                                                        ? 'border-acid bg-acid/5'
                                                        : 'border-border hover:border-acid/50'
                                                }`}
                                                onClick={() => loadMessages(conv.conversation_id)}
                                                data-testid={`conversation-${conv.conversation_id}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <Avatar className="w-10 h-10 border border-border">
                                                        <AvatarFallback className="bg-cyber-purple/20 text-cyber-purple font-display">
                                                            {conv.admin_name?.charAt(0) || conv.admin_company?.charAt(0) || 'E'}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="text-white text-sm font-medium">
                                                                    {conv.admin_name || 'Employer'}
                                                                </p>
                                                                {conv.admin_company && (
                                                                    <p className="text-muted-foreground text-xs">
                                                                        {conv.admin_company}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            {conv.unread_count > 0 && (
                                                                <Badge className="bg-neon-red text-white text-xs px-1.5 py-0">
                                                                    {conv.unread_count}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-muted-foreground text-xs truncate mt-1">
                                                            {conv.last_message || 'New conversation'}
                                                        </p>
                                                        <p className="text-muted-foreground/50 text-xs font-mono mt-1">
                                                            {formatDate(conv.last_message_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            )}
                        </CardContent>
                    </Card>

                    {/* Messages View */}
                    <Card className="bg-void-paper border-border lg:col-span-2">
                        <CardHeader className="pb-3 border-b border-border">
                            {selectedConversation ? (
                                <div className="flex items-center gap-3">
                                    <Avatar className="w-10 h-10 border border-acid">
                                        <AvatarFallback className="bg-acid/20 text-acid font-display">
                                            {selectedConversation.admin_name?.charAt(0) || 'E'}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <CardTitle className="font-display text-lg">
                                            {selectedConversation.admin_name || 'Employer'}
                                        </CardTitle>
                                        <p className="text-muted-foreground text-xs font-mono">
                                            {selectedConversation.admin_company || 'Regarding your profile'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <CardTitle className="font-display text-lg text-muted-foreground">
                                    Select a conversation
                                </CardTitle>
                            )}
                        </CardHeader>
                        <CardContent className="p-0">
                            {selectedConversation ? (
                                <div className="flex flex-col h-[500px]">
                                    {/* Messages */}
                                    <ScrollArea className="flex-1 p-4">
                                        <div className="space-y-4">
                                            {messages.map((msg) => (
                                                <div
                                                    key={msg.message_id}
                                                    className={`flex ${msg.sender_is_admin ? 'justify-start' : 'justify-end'}`}
                                                >
                                                    <div
                                                        className={`max-w-[80%] rounded-lg p-3 ${
                                                            msg.sender_is_admin
                                                                ? 'bg-void-subtle border border-border'
                                                                : 'bg-acid/10 border border-acid/30'
                                                        }`}
                                                    >
                                                        {msg.message_type === 'meeting_request' && (
                                                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
                                                                <Calendar className="w-4 h-4 text-cyber-purple" />
                                                                <span className="text-cyber-purple text-xs font-mono uppercase">
                                                                    Meeting Request
                                                                </span>
                                                            </div>
                                                        )}
                                                        <p className="text-sm text-white leading-relaxed">
                                                            {msg.content}
                                                        </p>
                                                        {msg.meeting_details?.proposed_time && (
                                                            <div className="mt-2 pt-2 border-t border-border/50">
                                                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                                    <Clock className="w-3 h-3" />
                                                                    Proposed: {msg.meeting_details.proposed_time}
                                                                </p>
                                                            </div>
                                                        )}
                                                        <p className="text-xs text-muted-foreground/50 mt-2 font-mono">
                                                            {formatDate(msg.created_at)}
                                                            {msg.read && !msg.sender_is_admin && (
                                                                <CheckCircle className="w-3 h-3 inline ml-2 text-acid" />
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>

                                    {/* Message Input */}
                                    <div className="p-4 border-t border-border">
                                        <div className="flex gap-3">
                                            <Input
                                                placeholder="Type your reply..."
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                                                className="bg-black/20 border-border focus:border-acid font-mono text-sm"
                                                data-testid="message-input"
                                            />
                                            <Button
                                                onClick={handleSendMessage}
                                                disabled={isSending || !newMessage.trim()}
                                                className="bg-acid text-black hover:bg-acid-hover"
                                                data-testid="send-message-btn"
                                            >
                                                {isSending ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <Send className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-[500px]">
                                    <div className="text-center">
                                        <MessageSquare className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
                                        <p className="text-muted-foreground">Select a conversation to view messages</p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
}
